import { type ComponentProps, useId } from "react";

import { cn } from "@/lib/utils";

const VARIANT_DURATION_SECONDS = 2.8;

type FlowConnectorVariant = "flow" | "dot";
type FlowConnectorLineStyle = "dashed" | "dotted";
type FlowConnectorClusterSide = "start" | "end";

const DASHED_STROKE = "10 14";
const DOTTED_STROKE = "1.5 6";

// Visible dot is a near-zero-length stroke whose round caps render as a
// circle of diameter strokeWidth. Pair with vector-effect="non-scaling-stroke"
// so the circle stays round even when the SVG is stretched non-uniformly.
const DOT_DASHARRAY = "0.01 1000";
const DOT_WIDTH = 4.5;

const STROKE_WIDTH = 1.8;
const STROKE_OPACITY = 0.55;

// Fraction of the viewBox over which the stroke fades to transparent at
// the cluster end. Keeps overlapping paths from compounding their alpha
// at the shared convergence point.
const FADE_FRACTION = 0.32;

type FlowConnectorProps = {
  /**
   * SVG path strings to render. Each path should fit in the 0-100
   * `viewBox`. Use `computeFlowPaths` to generate them analytically.
   */
  paths: readonly string[];
  /**
   * Animation style. `flow` drifts the dashed pattern uniformly along
   * every path. `dot` runs a circular packet along each path on top of
   * a static track. Defaults to `flow`.
   */
  variant?: FlowConnectorVariant;
  /**
   * Stroke pattern. `dashed` is the default for `flow`; `dotted` is the
   * default for `dot`. Override to mix and match.
   */
  lineStyle?: FlowConnectorLineStyle;
  /**
   * Which end of the path strings holds the single-point cluster. When
   * set, the stroke fades to transparent at that end so multiple paths
   * meeting there don't compound their opacity into a visible blob.
   */
  clusterSide?: FlowConnectorClusterSide;
  /**
   * When false, paths render statically. Defaults to true.
   */
  animated?: boolean;
} & Omit<ComponentProps<"svg">, "children">;

/**
 * Animated SVG connector for `FlowDiagram`. Renders each path as a
 * single clean dashed (or dotted) stroke. The atom is dumb — it draws
 * whatever path strings it's handed; `computeFlowPaths` decides the
 * geometry.
 *
 * Color defaults to `text-success`; override via `className` with any
 * `text-*` token to retheme.
 *
 * Reduced-motion is honored automatically: `motion-reduce:animate-none`
 * stops every animated stroke.
 */
function FlowConnector({
  paths,
  className,
  variant = "flow",
  lineStyle,
  clusterSide,
  animated = true,
  ...props
}: FlowConnectorProps) {
  const pathCount = paths.length;
  const resolvedLineStyle =
    lineStyle ?? (variant === "dot" ? "dotted" : "dashed");
  const trackDasharray =
    resolvedLineStyle === "dotted" ? DOTTED_STROKE : DASHED_STROKE;
  // Each line style gets its own keyframe so the per-cycle offset is an
  // integer multiple of that pattern's period; otherwise the dashes jump
  // at the loop boundary (e.g. dotted period 7.5 doesn't divide -24).
  const flowAnimationClass =
    resolvedLineStyle === "dotted"
      ? "animate-gateway-flow-dotted"
      : "animate-gateway-flow";

  const rawGradientId = useId();
  const gradientId = `flow-${rawGradientId.replace(/[^a-zA-Z0-9]/g, "")}`;
  const trackStroke = clusterSide
    ? `url(#${gradientId}-track)`
    : "currentColor";
  const dotStroke = clusterSide ? `url(#${gradientId}-dot)` : "currentColor";

  return (
    <svg
      aria-hidden
      className={cn("shrink-0 overflow-visible text-success", className)}
      data-slot="flow-connector"
      preserveAspectRatio="none"
      viewBox="0 0 100 100"
      {...props}
    >
      <title>Connection flow</title>
      {clusterSide ? (
        <defs>
          <linearGradient
            gradientUnits="userSpaceOnUse"
            id={`${gradientId}-track`}
            x1="0"
            x2="100"
            y1="50"
            y2="50"
          >
            {clusterSide === "start" ? (
              <>
                <stop offset="0" stopColor="currentColor" stopOpacity={0} />
                <stop
                  offset={FADE_FRACTION}
                  stopColor="currentColor"
                  stopOpacity={STROKE_OPACITY}
                />
                <stop
                  offset="1"
                  stopColor="currentColor"
                  stopOpacity={STROKE_OPACITY}
                />
              </>
            ) : (
              <>
                <stop
                  offset="0"
                  stopColor="currentColor"
                  stopOpacity={STROKE_OPACITY}
                />
                <stop
                  offset={1 - FADE_FRACTION}
                  stopColor="currentColor"
                  stopOpacity={STROKE_OPACITY}
                />
                <stop offset="1" stopColor="currentColor" stopOpacity={0} />
              </>
            )}
          </linearGradient>
          <linearGradient
            gradientUnits="userSpaceOnUse"
            id={`${gradientId}-dot`}
            x1="0"
            x2="100"
            y1="50"
            y2="50"
          >
            {clusterSide === "start" ? (
              <>
                <stop offset="0" stopColor="currentColor" stopOpacity={0} />
                <stop
                  offset={FADE_FRACTION}
                  stopColor="currentColor"
                  stopOpacity={1}
                />
                <stop offset="1" stopColor="currentColor" stopOpacity={1} />
              </>
            ) : (
              <>
                <stop offset="0" stopColor="currentColor" stopOpacity={1} />
                <stop
                  offset={1 - FADE_FRACTION}
                  stopColor="currentColor"
                  stopOpacity={1}
                />
                <stop offset="1" stopColor="currentColor" stopOpacity={0} />
              </>
            )}
          </linearGradient>
        </defs>
      ) : null}
      {/* Single phase across all paths keeps the dashes uniform when
       * multiple paths share trajectory near the cluster. The cluster-end
       * gradient fade handles the overlap brightness.
       *
       * `vector-effect="non-scaling-stroke"` keeps stroke width and dash
       * pattern in *screen* pixels so a connector that's short and wide
       * (e.g. a 1-row left side next to a 3-row right side) draws its
       * lines at the same thickness as its taller neighbour. Without
       * this, the Y-scale of the SVG would shrink the stroke
       * proportionally. */}
      {paths.map((d, index) => (
        <path
          className={cn(
            "motion-reduce:animate-none",
            animated && variant === "flow" && flowAnimationClass
          )}
          d={d}
          fill="none"
          // biome-ignore lint/suspicious/noArrayIndexKey: path strings can repeat across variants; index is the stable identity within a connector
          key={`path-${index}`}
          stroke={trackStroke}
          strokeDasharray={trackDasharray}
          strokeLinecap="round"
          strokeOpacity={clusterSide ? undefined : STROKE_OPACITY}
          strokeWidth={STROKE_WIDTH}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {animated && variant === "dot"
        ? paths.map((d, index) => (
            <path
              className="animate-gateway-flow-dot motion-reduce:animate-none"
              d={d}
              fill="none"
              // biome-ignore lint/suspicious/noArrayIndexKey: see above
              key={`dot-${index}`}
              // pathLength="100" normalises dasharray/dashoffset to a
              // % of path length, so the keyframe (which animates to
              // -120) clears the path at every rendered size despite
              // non-scaling-stroke pinning units to screen pixels.
              pathLength={100}
              stroke={dotStroke}
              strokeDasharray={DOT_DASHARRAY}
              strokeLinecap="round"
              strokeOpacity={clusterSide ? undefined : 1}
              strokeWidth={DOT_WIDTH}
              style={{
                animationDelay: `${(index * VARIANT_DURATION_SECONDS) / Math.max(pathCount, 1)}s`,
              }}
              vectorEffect="non-scaling-stroke"
            />
          ))
        : null}
    </svg>
  );
}

export { FlowConnector };
export type {
  FlowConnectorClusterSide,
  FlowConnectorLineStyle,
  FlowConnectorProps,
  FlowConnectorVariant,
};
